import { useState, useEffect, useMemo } from "react";
import { X, Search, Building2 } from "lucide-react";
import { useAdminAuth } from "../context/AdminAuthContext";
import { toast } from "sonner";

// ── Toggle Switch ───────────────────────────────────────────────────────────
function ToggleSwitch({ checked }) {
  return (
    <div
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 pointer-events-none ${
        checked ? "bg-[#C8102E]" : "bg-zinc-600"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </div>
  );
}

// ── Property Row ────────────────────────────────────────────────────────────
function PropertyRow({ prop, isAssigned, isExpanded, onToggle, onCardClick }) {
  const location = [prop.city, prop.country].filter(Boolean).join(", ");

  return (
    <div
      className={`border transition-all duration-150 ${
        isAssigned
          ? "border-zinc-700 bg-zinc-950"
          : "border-zinc-800/40 bg-[#080808]"
      }`}
      data-testid={`assignment-row-${prop.id}`}
    >
      {/* Main row */}
      <div
        className="flex items-center gap-4 px-5 py-3 cursor-pointer group select-none"
        onClick={onCardClick}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === "Enter" && onCardClick()}
        aria-expanded={isExpanded}
      >
        {/* Thumbnail */}
        <div className="w-20 h-14 flex-shrink-0 overflow-hidden bg-zinc-900">
          {prop.featured_image ? (
            <img
              src={prop.featured_image}
              alt={prop.name_en}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Building2 size={14} className="text-zinc-700" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          {prop.category_name && (
            <div className="text-[9px] tracking-[0.3em] text-[#C8102E] uppercase font-light mb-0.5">
              {prop.category_name}
            </div>
          )}
          <div className="text-white font-light text-sm truncate">{prop.name_en}</div>
          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
            {location && (
              <span className="text-[11px] text-white/40">{location}</span>
            )}
            {prop.property_type && (
              <span className="text-[11px] text-white/20">{prop.property_type}</span>
            )}
            {prop.internal_ref && (
              <span className="font-mono text-[10px] text-white/15">
                Ref. {prop.internal_ref}
              </span>
            )}
          </div>
        </div>

        {/* Status */}
        <div className="flex-shrink-0 w-20 text-right hidden md:block">
          <span
            className={`text-[9px] tracking-wider uppercase ${
              prop.status === "published" ? "text-emerald-500/50" : "text-zinc-700"
            }`}
          >
            {prop.status}
          </span>
        </div>

        {/* Toggle area */}
        <div
          className="flex-shrink-0 flex items-center gap-3 pl-5 border-l border-zinc-800"
          onClick={e => { e.stopPropagation(); onToggle(); }}
          role="button"
          tabIndex={0}
          onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onToggle(); } }}
          aria-label={isAssigned ? "Hide from investor" : "Show to investor"}
        >
          <span
            className={`text-[10px] tracking-wider uppercase w-12 text-right font-light ${
              isAssigned ? "text-white/50" : "text-zinc-600"
            }`}
          >
            {isAssigned ? "Visible" : "Hidden"}
          </span>
          <ToggleSwitch checked={isAssigned} />
        </div>
      </div>

      {/* Expanded preview */}
      {isExpanded && prop.description_en && (
        <div className="px-5 pb-4 ml-24 border-t border-zinc-800/30">
          <p className="text-white/35 text-xs leading-relaxed mt-3 line-clamp-3">
            {prop.description_en}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Main Modal ──────────────────────────────────────────────────────────────
export default function PropertyAssignmentModal({ user, onClose, onSaved }) {
  const { adminAxios } = useAdminAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assigned, setAssigned] = useState(new Set());
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    adminAxios
      .get(`/admin/users/${user.id}/assignments`)
      .then(({ data }) => {
        setProperties(data);
        setAssigned(new Set(data.filter(p => p.assigned).map(p => p.id)));
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  const categories = useMemo(
    () => [...new Set(properties.map(p => p.category_name).filter(Boolean))].sort(),
    [properties]
  );

  const filtered = useMemo(() => {
    let result = properties;
    if (activeFilter !== "All") {
      result = result.filter(p => p.category_name === activeFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter(
        p =>
          (p.name_en || "").toLowerCase().includes(q) ||
          (p.city || "").toLowerCase().includes(q) ||
          (p.country || "").toLowerCase().includes(q) ||
          (p.category_name || "").toLowerCase().includes(q) ||
          (p.internal_ref || "").toLowerCase().includes(q)
      );
    }
    return result;
  }, [properties, activeFilter, search]);

  const toggle = id =>
    setAssigned(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const save = async () => {
    setSaving(true);
    try {
      await adminAxios.put(`/admin/users/${user.id}/assignments`, {
        property_ids: [...assigned],
      });
      toast.success("Assignments updated successfully");
      onSaved?.();
      onClose();
    } catch { toast.error("Failed to update assignments"); }
    finally { setSaving(false); }
  };

  // Escape key
  useEffect(() => {
    const handle = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handle);
    return () => document.removeEventListener("keydown", handle);
  }, [onClose]);

  // Extracted to avoid nested JSX ternary
  const renderList = (isLoading, items, assignedSet, expandedId, setExpanded, onToggle) => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="w-px h-12 bg-[#C8102E] animate-pulse" />
        </div>
      );
    }
    if (items.length === 0) {
      return (
        <div className="flex items-center justify-center h-64 text-white/15 text-xs tracking-[0.4em] uppercase">
          No properties found
        </div>
      );
    }
    return (
      <div className="space-y-2 max-w-5xl mx-auto">
        {items.map(prop => (
          <PropertyRow
            key={prop.id}
            prop={prop}
            isAssigned={assignedSet.has(prop.id)}
            isExpanded={expandedId === prop.id}
            onToggle={() => onToggle(prop.id)}
            onCardClick={() => setExpanded(prev => prev === prop.id ? null : prop.id)}
          />
        ))}
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/96 flex flex-col"
      data-testid="assignment-modal"
    >
      {/* ── Sticky Header ── */}
      <div className="flex items-center justify-between px-8 py-4 bg-zinc-950 border-b border-zinc-800 flex-shrink-0">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-base font-serif font-light text-white tracking-wide">
              Assigned Opportunities
            </h2>
            <span
              className={`text-[9px] tracking-[0.25em] uppercase px-2.5 py-1 font-light ${
                assigned.size > 0
                  ? "bg-[#C8102E]/15 text-[#C8102E]"
                  : "bg-zinc-800 text-zinc-500"
              }`}
              data-testid="assigned-count-badge"
            >
              {assigned.size} {assigned.size === 1 ? "opportunity" : "opportunities"} assigned
            </span>
          </div>
          <p className="text-[11px] text-white/25 mt-0.5 tracking-wide">
            {user.first_name} {user.last_name}
            <span className="mx-2 text-zinc-800">·</span>
            Select which opportunities are visible to this investor
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button
            onClick={() => setAssigned(new Set(properties.map(p => p.id)))}
            data-testid="select-all-btn"
            className="text-[9px] text-white/35 hover:text-white tracking-[0.2em] uppercase px-3 py-1.5 border border-zinc-800 hover:border-zinc-600 transition-colors"
          >
            Select All
          </button>
          <button
            onClick={() => setAssigned(new Set())}
            data-testid="deselect-all-btn"
            className="text-[9px] text-white/35 hover:text-white tracking-[0.2em] uppercase px-3 py-1.5 border border-zinc-800 hover:border-zinc-600 transition-colors"
          >
            Deselect All
          </button>
          <div className="w-px h-5 bg-zinc-800 mx-1" />
          <button
            onClick={onClose}
            className="text-[9px] text-white/25 hover:text-white tracking-[0.2em] uppercase px-3 py-1.5 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-assignments-btn"
            className="bg-[#C8102E] hover:bg-[#A50D25] disabled:opacity-50 text-white text-[9px] tracking-[0.2em] uppercase px-6 py-2.5 transition-colors font-light"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
          <button
            onClick={onClose}
            className="ml-1 text-white/25 hover:text-white transition-colors p-1.5"
            aria-label="Close"
          >
            <X size={15} />
          </button>
        </div>
      </div>

      {/* ── Search + Filters ── */}
      <div className="flex items-center gap-3 px-8 py-3 bg-zinc-950 border-b border-zinc-800/40 flex-shrink-0 flex-wrap">
        <div className="relative">
          <Search size={11} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search property, location, category, ref..."
            data-testid="assignment-search"
            className="bg-zinc-900 border border-zinc-800 text-white/70 text-xs pl-8 pr-4 py-2 w-72 focus:outline-none focus:border-zinc-600 placeholder:text-white/15"
          />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {["All", ...categories].map(cat => (
            <button
              key={cat}
              onClick={() => setActiveFilter(cat)}
              data-testid={`filter-${cat.toLowerCase().replace(/ /g, "-")}`}
              className={`text-[9px] tracking-[0.2em] uppercase px-3 py-1.5 transition-colors ${
                activeFilter === cat
                  ? "bg-[#C8102E] text-white"
                  : "text-white/30 border border-zinc-800 hover:text-white hover:border-zinc-600"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
        <span className="ml-auto text-[10px] text-white/15 tracking-wider">
          {filtered.length} {filtered.length === 1 ? "property" : "properties"}
        </span>
      </div>

      {/* ── Property List ── */}
      <div className="flex-1 overflow-y-auto px-8 py-5">
        {renderList(loading, filtered, assigned, expandedId, setExpandedId, toggle)}
      </div>
    </div>
  );
}
